import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import api from "../../api/auth";
const fail = (e: any) =>
  message.error(e.response?.data?.message || e.message || "操作失败");
const BuildManagement: React.FC = () => {
  const [servers, setServers] = useState<any[]>([]),
    [profiles, setProfiles] = useState<any[]>([]),
    [templates, setTemplates] = useState<any[]>([]),
    [mappings, setMappings] = useState<any[]>([]);
  const [modal, setModal] = useState<{ kind: string; record?: any } | null>(
      null
    ),
    [saving, setSaving] = useState(false),
    [checking, setChecking] = useState<number | null>(null);
  const [form] = Form.useForm();
  const osFamily = Form.useWatch("osFamily", form);
  const buildType = Form.useWatch("buildType", form);
  const templateProfileId = Form.useWatch("profileId", form);
  const templateParameterKeys = (profileId: number) =>
    profiles.find((p) => p.id === profileId)?.buildType === "iso"
      ? ["app"]
      : ["edgec", "llcp", "wyos"];
  const load = useCallback(async () => {
    try {
      const r = await Promise.all(
        ["servers", "profiles", "templates", "server-templates"].map((p) =>
          api.get("/admin/build/" + p)
        )
      );
      setServers(r[0].data);
      setProfiles(r[1].data);
      setTemplates(r[2].data);
      setMappings(r[3].data);
    } catch (e) {
      fail(e);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const open = (kind: string, record?: any) => {
    form.resetFields();
    form.setFieldsValue(
      (kind === "templates" && record
        ? { ...record, ...record.defaultParameters }
        : record) || {
        sshPort: 22,
        sshUsername: "root",
        osFamily: "centos",
        osVersion: "7",
        architecture: "x86_64",
        maxConcurrency: 1,
        enabled: true,
        buildType: "iso",
        workspaceRoot: "/var/lib/llcp-build/jobs",
        artifactRoot: "/var/lib/llcp-build/artifacts",
      }
    );
    setModal({ kind, record });
  };
  const save = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const kind = modal!.kind;
      if (kind === "servers" || kind === "profiles") {
        if (kind === "profiles")
          values.runnerKey =
            (
              {
                centos: "centos7",
                debian: "debian13",
                ubuntu: "ubuntu2404",
              } as Record<string, string>
            )[values.osFamily] +
            "_" +
            values.buildType;
        const payload =
          kind === "profiles" && modal?.record
            ? { name: values.name, enabled: values.enabled }
            : values;
        if (modal?.record)
          await api.patch(`/admin/build/${kind}/${modal.record.id}`, payload);
        else await api.post(`/admin/build/${kind}`, payload);
      } else if (kind === "templates") {
        const defaultParameters: any = {};
        for (const key of templateParameterKeys(values.profileId))
          defaultParameters[key] = values[key];
        const payload = {
          defaultParameters,
          changeNote: values.changeNote || "",
        };
        if (modal?.record)
          await api.patch(`/admin/build/templates/${modal.record.id}`, payload);
        else
          await api.post("/admin/build/templates", {
            profileId: values.profileId,
            ...payload,
          });
      } else if (kind === "server-templates")
        await api.post(
          "/admin/build/server-templates",
          {
            serverId: values.serverId,
            templateVersionId: values.templateVersionId,
            templatePath: values.templatePath,
          },
          {
            timeout: 310000,
          }
        );
      setModal(null);
      message.success("保存成功");
      await load();
    } catch (e) {
      fail(e);
    } finally {
      setSaving(false);
    }
  };
  const profileOptions = profiles.map((p) => ({
    value: p.id,
    label: `${p.name} (${p.buildType.toUpperCase()})`,
  }));
  const serverTab = (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Button type="primary" onClick={() => open("servers")}>
        新增构建服务器
      </Button>
      <Table
        rowKey="id"
        dataSource={servers}
        scroll={{ x: 900 }}
        columns={[
          { title: "名称", dataIndex: "name" },
          {
            title: "SSH 地址",
            render: (_: any, s: any) =>
              `${s.sshUsername}@${s.host}:${s.sshPort}`,
          },
          {
            title: "系统",
            render: (_: any, s: any) =>
              `${s.osFamily} ${s.osVersion} ${s.architecture}`,
          },
          {
            title: "启用",
            dataIndex: "enabled",
            render: (v: boolean) => (
              <Tag color={v ? "green" : "default"}>{v ? "启用" : "停用"}</Tag>
            ),
          },
          {
            title: "下载前缀",
            dataIndex: "downloadUrlPrefix",
            render: (v: string) => v || "未配置",
          },
          {
            title: "操作",
            render: (_: any, s: any) => (
              <Space>
                <Button onClick={() => open("servers", s)}>编辑</Button>
                <Button
                  loading={checking === s.id}
                  onClick={async () => {
                    setChecking(s.id);
                    try {
                      const r = await api.post(
                        `/admin/build/servers/${s.id}/check`,
                        {},
                        { timeout: 35000 }
                      );
                      Modal.info({
                        title: r.data.success ? "检查通过" : "检查失败",
                        content: (
                          <pre style={{ whiteSpace: "pre-wrap" }}>
                            {r.data.output}
                          </pre>
                        ),
                      });
                      load();
                    } catch (e) {
                      fail(e);
                    } finally {
                      setChecking(null);
                    }
                  }}
                >
                  检查连接
                </Button>
              </Space>
            ),
          },
        ]}
      />
    </Space>
  );
  const profileTab = (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Button type="primary" onClick={() => open("profiles")}>
        新增构建方案
      </Button>
      <Table
        rowKey="id"
        dataSource={profiles}
        columns={[
          { title: "名称", dataIndex: "name" },
          { title: "类型", dataIndex: "buildType" },
          {
            title: "目标系统",
            render: (_: any, p: any) =>
              `${p.osFamily} ${p.osVersion} / ${p.architecture}`,
          },
          { title: "默认模板 ID", dataIndex: "activeTemplateVersionId" },
          {
            title: "状态",
            dataIndex: "enabled",
            render: (v: boolean) => (v ? "启用" : "停用"),
          },
          {
            title: "默认模板",
            render: (_: any, p: any) => (
              <Select
                style={{ width: 180 }}
                placeholder="选择已发布模板"
                value={p.activeTemplateVersionId || undefined}
                options={templates
                  .filter(
                    (t) => t.profileId === p.id && t.status === "published"
                  )
                  .map((t) => ({
                    value: t.id,
                    label: `v${t.versionNo} (ID ${t.id})`,
                  }))}
                onChange={async (id) => {
                  try {
                    await api.post(`/admin/build/profiles/${p.id}/activate`, {
                      templateVersionId: id,
                    });
                    message.success("默认模板已切换");
                    load();
                  } catch (e) {
                    fail(e);
                  }
                }}
              />
            ),
          },
          {
            title: "操作",
            render: (_: any, p: any) => (
              <Button onClick={() => open("profiles", p)}>编辑</Button>
            ),
          },
        ]}
      />
    </Space>
  );
  const templateTab = (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Alert
        showIcon
        type="info"
        message="每个系统和构建类型使用固定目录。脚本、二进制直接在服务器目录中更新，无需新建模板版本或重新登记；下次任务会读取当前内容并保存输入快照。默认参数可直接编辑。"
      />
      <Space>
        <Button type="primary" onClick={() => open("templates")}>
          创建构建模板
        </Button>
        <Button onClick={() => open("server-templates")}>
          登记 / 检查固定目录
        </Button>
      </Space>
      <Table
        rowKey="id"
        dataSource={templates.filter((t) => t.status !== "retired")}
        columns={[
          { title: "ID", dataIndex: "id" },
          {
            title: "方案",
            dataIndex: "profileId",
            render: (id: number) =>
              profiles.find((p) => p.id === id)?.name || id,
          },
          { title: "配置编号", dataIndex: "versionNo" },
          { title: "状态", dataIndex: "status" },
          {
            title: "默认参数",
            dataIndex: "defaultParameters",
            render: (v: any, t: any) =>
              JSON.stringify(
                Object.fromEntries(
                  Object.entries(v).filter(([key]) =>
                    templateParameterKeys(t.profileId).includes(key)
                  )
                )
              ),
          },
          { title: "说明", dataIndex: "changeNote" },
          {
            title: "操作",
            render: (_: any, t: any) =>
              t.status !== "retired" && (
                <Button onClick={() => open("templates", t)}>编辑参数</Button>
              ),
          },
        ]}
      />
      <Typography.Title level={5}>服务器模板</Typography.Title>
      <Table
        rowKey="id"
        dataSource={mappings.filter((m) => m.enabled)}
        columns={[
          {
            title: "服务器",
            dataIndex: "serverId",
            render: (id: number) =>
              servers.find((s) => s.id === id)?.name || id,
          },
          { title: "模板 ID", dataIndex: "templateVersionId" },
          { title: "固定目录", dataIndex: "templatePath" },
          {
            title: "操作",
            render: (_: any, m: any) => (
              <Button onClick={() => open("server-templates", m)}>
                编辑 / 检查
              </Button>
            ),
          },
          { title: "状态", dataIndex: "deploymentStatus" },
        ]}
      />
    </Space>
  );
  return (
    <>
      <Typography.Title level={3}>构建管理</Typography.Title>
      <Tabs
        items={[
          { key: "servers", label: "构建服务器", children: serverTab },
          { key: "profiles", label: "构建方案", children: profileTab },
          {
            key: "templates",
            label: "构建模板 / 固定目录",
            children: templateTab,
          },
        ]}
      />
      <Modal
        width={680}
        title={
          modal?.kind === "servers"
            ? "构建服务器"
            : modal?.kind === "profiles"
            ? "构建方案"
            : modal?.kind === "templates"
            ? modal.record
              ? "编辑模板参数"
              : "创建构建模板"
            : "登记服务器模板"
        }
        open={!!modal}
        onCancel={() => {
          if (!saving) setModal(null);
        }}
        onOk={save}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          {modal?.kind === "servers" && (
            <>
              {[
                ["name", "名称"],
                ["host", "SSH 地址"],
                ["sshUsername", "SSH 用户名"],
                ["credentialRef", "LLCP 本机 SSH 私钥绝对路径"],
                ["hostKeyFingerprint", "SSH 主机指纹（SHA256 十六进制）"],
                ["osFamily", "宿主系统类型"],
                ["osVersion", "宿主系统版本"],
                ["architecture", "架构"],
                ["workspaceRoot", "独立任务根目录"],
                ["artifactRoot", "成品根目录"],
              ].map(([name, label]) => (
                <Form.Item
                  key={name}
                  name={name}
                  label={label}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              ))}
              <Form.Item
                name="sshPort"
                label="SSH 端口"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={65535} />
              </Form.Item>
              <Form.Item
                name="maxConcurrency"
                label="最大并发"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={8} />
              </Form.Item>
              <Form.Item
                name="downloadUrlPrefix"
                label="下载地址前缀（可后期填写）"
              >
                <Input placeholder="https://iso.example.com/builds/" />
              </Form.Item>
              <Form.Item name="enabled" label="启用" valuePropName="checked">
                <Switch />
              </Form.Item>
            </>
          )}
          {modal?.kind === "profiles" && (
            <>
              <Form.Item
                name="name"
                label="方案名称"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="osFamily"
                label="目标系统"
                rules={[{ required: true }]}
              >
                <Select
                  disabled={!!modal.record}
                  options={[
                    { value: "centos", label: "CentOS 7" },
                    { value: "debian", label: "Debian 13" },
                    { value: "ubuntu", label: "Ubuntu 24.04" },
                  ]}
                  onChange={(family) =>
                    form.setFieldsValue({
                      osVersion:
                        family === "centos"
                          ? "7"
                          : family === "debian"
                          ? "13"
                          : "24.04",
                      architecture: family === "centos" ? "x86_64" : "amd64",
                      buildType:
                        buildType === "iso"
                          ? "iso"
                          : family === "centos"
                          ? "rpm"
                          : "deb",
                    })
                  }
                />
              </Form.Item>
              <Form.Item
                name="buildType"
                label="构建类型"
                rules={[{ required: true }]}
              >
                <Select
                  disabled={!!modal.record}
                  options={[
                    {
                      value: osFamily === "centos" ? "rpm" : "deb",
                      label: osFamily === "centos" ? "RPM" : "DEB",
                    },
                    { value: "iso", label: "ISO" },
                  ]}
                />
              </Form.Item>
              <Form.Item name="osVersion" label="目标系统版本">
                <Input readOnly />
              </Form.Item>
              <Form.Item name="architecture" label="目标架构">
                <Input readOnly />
              </Form.Item>
              <Form.Item name="enabled" label="启用" valuePropName="checked">
                <Switch />
              </Form.Item>
            </>
          )}
          {modal?.kind === "templates" && (
            <>
              <Form.Item
                name="profileId"
                label="所属方案"
                rules={[{ required: true }]}
              >
                <Select options={profileOptions} disabled={!!modal?.record} />
              </Form.Item>
              {templateParameterKeys(templateProfileId).map((key) => (
                <Form.Item
                  key={key}
                  name={key}
                  label={`默认 --${key}`}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              ))}
              <Form.Item name="changeNote" label="修改说明">
                <Input.TextArea />
              </Form.Item>
            </>
          )}
          {modal?.kind === "server-templates" && (
            <>
              <Form.Item
                name="serverId"
                label="服务器"
                rules={[{ required: true }]}
              >
                <Select
                  options={servers.map((s) => ({ value: s.id, label: s.name }))}
                />
              </Form.Item>
              <Form.Item
                name="templateVersionId"
                label="构建模板"
                rules={[{ required: true }]}
              >
                <Select
                  options={templates
                    .filter((t) => t.status !== "retired")
                    .map((t) => ({
                      value: t.id,
                      label: `${
                        profiles.find((p) => p.id === t.profileId)?.name ||
                        t.profileId
                      } v${t.versionNo} (ID ${t.id})`,
                    }))}
                />
              </Form.Item>
              <Form.Item
                name="templatePath"
                label="构建服务器上的固定模板绝对路径"
                rules={[{ required: true }]}
              >
                <Input placeholder="/var/lib/llcp-build/templates/centos7-rpm" />
              </Form.Item>
              <Alert
                type="info"
                message="首次配置需准备好固定目录。登记检查当前脚本和文件，不执行正式构建。后续更新原目录即可，采用临时文件上传完成后原子替换。"
              />
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};
export default BuildManagement;
